import { Fragment, useMemo, useRef, type ReactNode } from 'react';
import {
  Badge,
  ColumnManager,
  EmptyState,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type BadgeTone,
  type FrozenSide,
} from '@/components/ui';
import { InlineField, type InlineSaveResult } from '@/components/ui/InlineField';
import { Money } from '@/components/ui/Money';
import { api, ApiError } from '@/lib/api';
import { cx } from '@/lib/cx';
import {
  EDITABLE_LINE_FIELDS,
  LINE_NOTES_MAX_LENGTH,
  achievedTone,
  formatCount,
  formatPct,
  groupLines,
  isDecimalString,
  parseLineFieldInput,
  subtotalLines,
  subtractDecimal,
  type SalesTargetFieldMeta,
  type SalesTargetNumericField,
  type SalesTargetTone,
} from '@/lib/sales-targets';
import type { SalesTargetLine, SalesTargetPool, SalesTargetsView } from '@/lib/types';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';

/**
 * Sales targets, desktop (`md`+) grouped table (owner 2026-10-28). The panel
 * renders it inside `hidden md:block`; `SalesTargetsCards` is the phone list.
 *
 * Rows: one group per product family (`groupLines`), each with a header row and
 * a subtotal row; then the pools (read-only here, edited on the pools card);
 * then the grand total, which is the SERVER's `view.totals` (never re-summed
 * across groups here, so it cannot drift from the sheet).
 *
 * ⚠️ Pool members: their own plan/target are excluded from every total (the
 * pool row carries them). So a member row shows a pool badge and its OWN
 * achieved only; its revenue / behind / % cells read '—'. Its inputs stay
 * editable (they are still stored on the line).
 *
 * Edits: `InlineField` → `api.upsertSalesTargetLine({cycleId, projectId,
 * field})`, then `onChanged()` (the panel re-reads the view; nothing is
 * patched optimistically, because every figure is derived server-side).
 *
 * Column ids are stored (`columns.sales-targets.table` hides) — never rename.
 */

export interface SalesTargetsTableProps {
  view: SalesTargetsView;
  canEdit: boolean;
  onChanged: () => void;
}

const SCOPE = 'sales-targets.table';

type ColumnKey =
  | 'project'
  | 'region'
  | 'intake'
  | SalesTargetNumericField
  | 'plannedRevenue'
  | 'targetRevenue'
  | 'achieved'
  | 'behind'
  | 'pct'
  | 'sold'
  | 'notes';

const COLUMN_LABELS: Record<ColumnKey, string> = {
  project: 'Project',
  region: 'Region',
  intake: 'Intake',
  institutionPlanned: 'Planned capacity · Institution',
  partnerPlanned: 'Planned capacity · Partner',
  institutionTarget: 'Target · Institution',
  partnerTarget: 'Target · Partner',
  travelPercent: 'Travel %',
  months: 'Months',
  unitPriceOverrideUsd: 'Unit price USD',
  plannedRevenue: 'Planned revenue',
  targetRevenue: 'Target revenue',
  achieved: 'Achieved',
  behind: 'Behind',
  pct: '%',
  sold: 'Sold (inst/partner)',
  notes: 'Notes',
};

const COLUMN_KEYS: readonly ColumnKey[] = [
  'project',
  'region',
  'intake',
  'institutionPlanned',
  'partnerPlanned',
  'institutionTarget',
  'partnerTarget',
  'travelPercent',
  'months',
  'unitPriceOverrideUsd',
  'plannedRevenue',
  'targetRevenue',
  'achieved',
  'behind',
  'pct',
  'sold',
  'notes',
];

/**
 * Two-level header (owner 2026-10-28): the four count columns sit under a
 * "Planned capacity" and a "Target" band, each split Institution | Partner.
 * Every other column spans both header rows. Bands are built from the LIVE
 * visible order (`headerBands`), so a hidden or reordered count column still
 * yields an honest header: consecutive members of one band merge, a lone
 * member gets a band of its own.
 */
type HeaderBandKey = 'planned' | 'target';

const HEADER_BANDS: Partial<Record<ColumnKey, { band: HeaderBandKey; sub: string }>> = {
  institutionPlanned: { band: 'planned', sub: 'Institution' },
  partnerPlanned: { band: 'planned', sub: 'Partner' },
  institutionTarget: { band: 'target', sub: 'Institution' },
  partnerTarget: { band: 'target', sub: 'Partner' },
};

const BAND_LABELS: Record<HeaderBandKey, string> = {
  planned: 'Planned capacity',
  target: 'Target',
};

type HeaderCell =
  | { kind: 'single'; key: ColumnKey }
  | { kind: 'band'; band: HeaderBandKey; keys: ColumnKey[] };

function headerBands(keys: readonly ColumnKey[]): HeaderCell[] {
  const cells: HeaderCell[] = [];
  for (const key of keys) {
    const band = HEADER_BANDS[key]?.band;
    const last = cells[cells.length - 1];
    if (band === undefined) cells.push({ kind: 'single', key });
    else if (last?.kind === 'band' && last.band === band) last.keys.push(key);
    else cells.push({ kind: 'band', band, keys: [key] });
  }
  return cells;
}

const LOCKED_KEYS: readonly ColumnKey[] = ['project'];
const PINNED_LEFT = { pinnedLeftKeys: ['project'] } as const;

const NUMERIC_KEYS = new Set<string>([
  ...EDITABLE_LINE_FIELDS.map((field) => field.key),
  'plannedRevenue',
  'targetRevenue',
  'achieved',
  'behind',
  'pct',
  'sold',
]);

const FIELD_META = new Map<string, SalesTargetFieldMeta>(
  EDITABLE_LINE_FIELDS.map((field): [string, SalesTargetFieldMeta] => [field.key, field]),
);

const TONE_TO_BADGE: Record<SalesTargetTone, BadgeTone> = {
  success: 'success',
  neutral: 'neutral',
  warning: 'warning',
  danger: 'danger',
};

const DASH = <span className="text-fg-subtle">—</span>;

const isColumnKey = (key: string): key is ColumnKey =>
  Object.prototype.hasOwnProperty.call(COLUMN_LABELS, key);

/** A stored decimal as the editor's starting text: "32.00" → "32", null → ''. */
function editValue(value: string | null): string {
  if (!isDecimalString(value)) return '';
  const trimmed = value.trim();
  return trimmed.includes('.') ? trimmed.replace(/\.?0+$/, '') : trimmed;
}

/** Exact cent units of a decimal string (rounded half away from zero), or null. */
function toCents(value: string): bigint | null {
  if (!isDecimalString(value)) return null;
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const [whole = '0', fraction = ''] = (negative ? trimmed.slice(1) : trimmed).split('.');
  let units = BigInt(whole) * 100n + BigInt((fraction + '00').slice(0, 2));
  if ((fraction[2] ?? '0') >= '5') units += 1n;
  return negative ? -units : units;
}

/**
 * achieved ÷ target × 100 to one decimal, exact (BigInt). For subtotal rows,
 * which the server does not send. `null` when either is missing or target ≤ 0.
 */
function pctOf(achieved: string | null, target: string | null): string | null {
  if (achieved === null || target === null) return null;
  const a = toCents(achieved);
  const t = toCents(target);
  if (a === null || t === null || t <= 0n) return null;
  const negative = a < 0n;
  const tenths = ((negative ? -a : a) * 1000n * 2n + t) / (t * 2n);
  const text = `${tenths / 10n}.${tenths % 10n}`;
  return negative && tenths !== 0n ? `-${text}` : text;
}

function frozenProps(
  key: string,
  frozenSideOf: (key: string) => FrozenSide | undefined,
  offsets: Record<string, number>,
): { frozen?: FrozenSide; frozenOffset?: number } {
  const side = frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: offsets[key] ?? 0 };
}

function PctBadge({ pct, tone }: { pct: string | null; tone: SalesTargetTone | null }) {
  if (!isDecimalString(pct)) return DASH;
  if (tone === null) return <span className="tabular-nums">{formatPct(pct)}</span>;
  return (
    <Badge tone={TONE_TO_BADGE[tone]} className="tabular-nums">
      {formatPct(pct)}
    </Badge>
  );
}

function usd(amount: string | null): ReactNode {
  return isDecimalString(amount) ? <Money amount={amount} currency="USD" /> : DASH;
}

/** The write for one cell, reporting rather than throwing (InlineField contract). */
async function saveField(
  cycleId: number,
  projectId: number,
  key: SalesTargetNumericField | 'notes',
  value: string | null,
  onChanged: () => void,
): Promise<InlineSaveResult> {
  try {
    await api.upsertSalesTargetLine({ cycleId, projectId, [key]: value });
    onChanged();
    return { status: 'saved' };
  } catch (caught) {
    if (caught instanceof ApiError) {
      if (caught.status === 403) {
        return {
          status: 'error',
          message:
            'Your permission set does not include this. An administrator can change that. Nothing was saved.',
        };
      }
      if (caught.status === 404) {
        return {
          status: 'error',
          message: 'This project is no longer in this cycle, so the change could not be saved.',
        };
      }
      const { fields, unroutable } = caught.routeFieldErrors([key]);
      const fieldText = Object.values(fields).join(' ');
      if (fieldText !== '') return { status: 'error', message: fieldText };
      if (unroutable.length > 0) {
        return { status: 'warning', message: `${unroutable.join(' ')} Nothing was saved.` };
      }
      return { status: 'error', message: caught.formMessage ?? caught.message };
    }
    if (caught instanceof Error && caught.message !== '') {
      return { status: 'error', message: caught.message };
    }
    return {
      status: 'error',
      message: 'Could not reach the server. Check your connection and try again.',
    };
  }
}

export function SalesTargetsTable({ view, canEdit, onChanged }: SalesTargetsTableProps) {
  const cycleId = view.cycle.id;
  const groups = useMemo(() => groupLines(view.lines), [view.lines]);
  const poolById = useMemo(
    () => new Map(view.pools.map((pool): [number, SalesTargetPool] => [pool.id, pool])),
    [view.pools],
  );

  const layout = useColumnLayout(SCOPE, COLUMN_KEYS, LOCKED_KEYS, [], PINNED_LEFT);
  const { frozenSideOf } = layout;
  const visibleKeys = layout.visibleKeys.filter(isColumnKey);
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const offsets = useFrozenOffsets(headRowRef, visibleKeys, frozenSideOf);
  const labelOf = (key: string) => (isColumnKey(key) ? COLUMN_LABELS[key] : key);
  const fp = (key: string) => frozenProps(key, frozenSideOf, offsets);
  const restSpan = Math.max(visibleKeys.length - 1, 1);
  const headerCells = headerBands(visibleKeys);
  const hasBands = headerCells.some((cell) => cell.kind === 'band');

  /** A band heading freezes only when every column under it freezes to one side. */
  const bandFrozenProps = (keys: readonly ColumnKey[]) => {
    const first = keys[0];
    const last = keys[keys.length - 1];
    if (first === undefined || last === undefined) return {};
    const side = frozenSideOf(first);
    if (side === undefined || keys.some((key) => frozenSideOf(key) !== side)) return {};
    return fp(side === 'left' ? first : last);
  };

  /* ---- line cells ---- */

  function numericCell(line: SalesTargetLine, meta: SalesTargetFieldMeta): ReactNode {
    const stored = meta.key === 'unitPriceOverrideUsd' ? line.unitPriceOverrideUsd : line[meta.key];
    let display: ReactNode;
    if (meta.key === 'unitPriceOverrideUsd') {
      display =
        isDecimalString(line.unitPriceUsd) ? (
          <span className="inline-flex items-baseline justify-end gap-1.5 whitespace-nowrap">
            <Money amount={line.unitPriceUsd} currency="USD" />
            {line.unitPriceSource !== null && (
              <span
                className={cx(
                  'text-[0.7rem]',
                  line.unitPriceSource === 'override' ? 'text-accent-brand' : 'text-fg-subtle',
                )}
              >
                {line.unitPriceSource === 'override' ? 'Override' : 'Price book'}
              </span>
            )}
          </span>
        ) : (
          DASH
        );
    } else if (meta.kind === 'percent') {
      display = isDecimalString(stored) ? <span className="tabular-nums">{formatPct(stored)}</span> : DASH;
    } else {
      display = isDecimalString(stored) ? (
        <span className="tabular-nums">{formatCount(stored)}</span>
      ) : (
        DASH
      );
    }
    return (
      <InlineField
        label={`${COLUMN_LABELS[meta.key]} for ${line.projectName}`}
        value={editValue(stored)}
        display={display}
        disabled={!canEdit}
        inputMode="decimal"
        validate={(next) => {
          const parsed = parseLineFieldInput(meta, next);
          return parsed.ok ? null : parsed.error;
        }}
        onSave={(next) => {
          const parsed = parseLineFieldInput(meta, next);
          if (!parsed.ok) return Promise.resolve({ status: 'error', message: parsed.error });
          return saveField(cycleId, line.projectId, meta.key, parsed.value, onChanged);
        }}
      />
    );
  }

  function lineCell(line: SalesTargetLine, key: ColumnKey): ReactNode {
    const member = line.poolId !== null;
    const meta = FIELD_META.get(key);
    if (meta) return numericCell(line, meta);
    switch (key) {
      case 'project': {
        const pool = line.poolId === null ? undefined : poolById.get(line.poolId);
        return (
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="font-medium text-fg">{line.projectName}</span>
            {member && (
              <span>
                <Badge tone="info" variant="outline">
                  {pool ? `Pool: ${pool.name}` : 'Pool'}
                </Badge>
              </span>
            )}
          </span>
        );
      }
      case 'region':
        return line.regions.length > 0 ? line.regions.join(', ') : DASH;
      case 'intake':
        return line.intake ?? DASH;
      case 'plannedRevenue':
        return member ? DASH : usd(line.plannedRevenueUsd);
      case 'targetRevenue':
        return member ? DASH : usd(line.targetRevenueUsd);
      case 'achieved':
        return usd(line.achievedUsd);
      case 'behind':
        return member ? DASH : usd(line.gapUsd);
      case 'pct':
        return member ? DASH : <PctBadge pct={line.achievedPct} tone={achievedTone(line)} />;
      case 'sold':
        return (
          <span className="tabular-nums">
            {line.institutionsSold} / {line.partnersSold}
          </span>
        );
      case 'notes':
        return (
          <InlineField
            label={`Notes for ${line.projectName}`}
            kind="multiline"
            rows={3}
            value={line.notes ?? ''}
            disabled={!canEdit}
            maxLength={LINE_NOTES_MAX_LENGTH}
            onSave={(next) =>
              saveField(cycleId, line.projectId, 'notes', next === '' ? null : next, onChanged)
            }
          />
        );
      default:
        return DASH;
    }
  }

  /* ---- pool cells (read-only; edited on the pools card) ---- */

  function poolCell(pool: SalesTargetPool, key: ColumnKey): ReactNode {
    const count = pool.kind === 'count';
    switch (key) {
      case 'project':
        return (
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="font-medium text-fg">{pool.name}</span>
            <span>
              <Badge tone="info" variant="outline">
                {count ? 'Count pool' : 'Revenue pool'}
              </Badge>
            </span>
          </span>
        );
      case 'institutionPlanned':
        return count && isDecimalString(pool.plannedCount) ? (
          <span className="tabular-nums">{formatCount(pool.plannedCount)}</span>
        ) : (
          DASH
        );
      case 'institutionTarget':
        return count && isDecimalString(pool.targetCount) ? (
          <span className="tabular-nums">{formatCount(pool.targetCount)}</span>
        ) : (
          DASH
        );
      case 'unitPriceOverrideUsd':
        return count ? usd(pool.unitPriceUsd) : DASH;
      case 'plannedRevenue':
        return usd(pool.plannedRevenueUsd);
      case 'targetRevenue':
        return usd(pool.targetRevenueUsd);
      case 'achieved':
        return usd(pool.achievedUsd);
      case 'behind':
        return usd(pool.gapUsd);
      case 'pct':
        return <PctBadge pct={pool.achievedPct} tone={achievedTone(pool)} />;
      default:
        return DASH;
    }
  }

  /* ---- summary rows (subtotal / grand total) ---- */

  function summaryCells(
    label: string,
    figures: {
      plannedRevenueUsd: string | null;
      targetRevenueUsd: string | null;
      achievedUsd: string | null;
      gapUsd: string | null;
      pct: string | null;
      tone: SalesTargetTone | null;
    },
  ): ReactNode {
    return visibleKeys.map((key) => {
      let content: ReactNode = null;
      switch (key) {
        case 'project':
          content = label;
          break;
        case 'plannedRevenue':
          content = usd(figures.plannedRevenueUsd);
          break;
        case 'targetRevenue':
          content = usd(figures.targetRevenueUsd);
          break;
        case 'achieved':
          content = usd(figures.achievedUsd);
          break;
        case 'behind':
          content = usd(figures.gapUsd);
          break;
        case 'pct':
          content = <PctBadge pct={figures.pct} tone={figures.tone} />;
          break;
        default:
          content = null;
      }
      return (
        <TD
          key={key}
          numeric={NUMERIC_KEYS.has(key)}
          className="whitespace-nowrap font-semibold"
          {...fp(key)}
        >
          {content}
        </TD>
      );
    });
  }

  const totals = view.totals;
  const hasRows = view.lines.length > 0 || view.pools.length > 0;

  if (!hasRows) {
    return <EmptyState title={`No projects in ${view.cycle.name} yet.`} />;
  }

  return (
    <div className="min-w-0">
      <div className="flex items-center justify-end gap-2 pb-2">
        {!layout.loading && (
          <ColumnManager layout={layout} labelOf={labelOf} lockedKeys={LOCKED_KEYS} />
        )}
      </div>
      <TableWrap className="rounded-md border border-border">
        <Table>
          <THead>
            {hasBands ? (
              <>
                <TR>
                  {headerCells.map((cell) =>
                    cell.kind === 'single' ? (
                      <TH
                        key={cell.key}
                        rowSpan={2}
                        numeric={NUMERIC_KEYS.has(cell.key)}
                        className="align-bottom"
                        {...fp(cell.key)}
                      >
                        {COLUMN_LABELS[cell.key]}
                      </TH>
                    ) : (
                      <TH
                        key={`band:${cell.band}`}
                        colSpan={cell.keys.length}
                        className="border-b border-border text-center"
                        {...bandFrozenProps(cell.keys)}
                      >
                        {BAND_LABELS[cell.band]}
                      </TH>
                    ),
                  )}
                </TR>
                <TR>
                  {headerCells.flatMap((cell) =>
                    cell.kind === 'band'
                      ? cell.keys.map((key) => (
                          <TH key={key} numeric {...fp(key)}>
                            {HEADER_BANDS[key]?.sub ?? COLUMN_LABELS[key]}
                          </TH>
                        ))
                      : [],
                  )}
                </TR>
                {/*
                 * Measuring row for `useFrozenOffsets`: it reads one cell per
                 * visible key, which neither banded row has. Zero height.
                 */}
                <tr ref={headRowRef} aria-hidden="true">
                  {visibleKeys.map((key) => (
                    <td key={key} className="h-0 p-0" />
                  ))}
                </tr>
              </>
            ) : (
              <TR ref={headRowRef}>
                {visibleKeys.map((key) => (
                  <TH key={key} numeric={NUMERIC_KEYS.has(key)} {...fp(key)}>
                    {COLUMN_LABELS[key]}
                  </TH>
                ))}
              </TR>
            )}
          </THead>
          <TBody>
            {groups.map((group) => {
              const sub = subtotalLines(group.lines);
              return (
                <Fragment key={group.key}>
                  <TR>
                    <TD className="font-semibold text-fg-secondary" {...fp('project')}>
                      {group.label}
                    </TD>
                    {visibleKeys.length > 1 && <TD colSpan={restSpan} />}
                  </TR>
                  {group.lines.map((line) => (
                    <TR key={line.projectId} interactive>
                      {visibleKeys.map((key) => (
                        <TD
                          key={key}
                          numeric={NUMERIC_KEYS.has(key)}
                          verticalAlign="top"
                          className={cx(
                            key === 'notes' ? 'min-w-56' : 'whitespace-nowrap',
                            key === 'project' && 'min-w-56',
                          )}
                          {...fp(key)}
                        >
                          {lineCell(line, key)}
                        </TD>
                      ))}
                    </TR>
                  ))}
                  <TR>
                    {summaryCells(`${group.label} subtotal`, {
                      ...sub,
                      gapUsd: subtractDecimal(sub.targetRevenueUsd, sub.achievedUsd),
                      pct: pctOf(sub.achievedUsd, sub.targetRevenueUsd),
                      tone: null,
                    })}
                  </TR>
                </Fragment>
              );
            })}

            {view.pools.length > 0 && (
              <>
                <TR>
                  <TD className="font-semibold text-fg-secondary" {...fp('project')}>
                    Pools
                  </TD>
                  {visibleKeys.length > 1 && <TD colSpan={restSpan} />}
                </TR>
                {view.pools.map((pool) => (
                  <TR key={`pool:${pool.id}`} interactive>
                    {visibleKeys.map((key) => (
                      <TD
                        key={key}
                        numeric={NUMERIC_KEYS.has(key)}
                        className="whitespace-nowrap"
                        {...fp(key)}
                      >
                        {poolCell(pool, key)}
                      </TD>
                    ))}
                  </TR>
                ))}
              </>
            )}

            <TR className="border-t-2 border-border">
              {summaryCells('Total', {
                plannedRevenueUsd: totals.plannedRevenueUsd,
                targetRevenueUsd: totals.targetRevenueUsd,
                achievedUsd: totals.achievedUsd,
                gapUsd: totals.gapUsd,
                pct: totals.achievedPct,
                tone: achievedTone(totals),
              })}
            </TR>
          </TBody>
        </Table>
      </TableWrap>
    </div>
  );
}
